import { useState, useEffect } from 'react'
import Table from '../../components/Table'
import { VenueDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useSearch } from '../../contexts/SearchContext'
import Pagination from '../../components/Pagination'
import AddVenueModal from '../../components/modals/AddVenueModal'
import VenueSuggestionsTable from './VenueSuggestionsTable'

type VenuesTab = 'active' | 'closed' | 'suggestions'

const VENUE_TABS: Array<{ key: VenuesTab; label: string; status: string }> = [
  { key: 'active', label: 'Active', status: 'open' },
  { key: 'closed', label: 'Closed', status: 'closed' },
  { key: 'suggestions', label: 'Suggestions', status: 'suggestions' },
]

// Calculate optimal page size based on viewport height
function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56 // Estimated table row height
  const headerHeight = 172 // Estimated table header height
  const paginationHeight = 60 // Estimated pagination controls height

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)

  // Clamp between 10 and 100
  return Math.max(10, Math.min(100, calculatedPageSize))
}

function resolveTab(statusParam: string): VenuesTab {
  const normalized = statusParam.trim().toLowerCase()
  if (normalized === 'closed') return 'closed'
  if (normalized === 'suggestions') return 'suggestions'
  return 'active'
}

function resolveStatusFilter(statusParam: string, tab: VenuesTab): string {
  const normalized = statusParam.trim().toLowerCase()
  // Dashboard deep-link: venues with visits in the last 30 days
  if (normalized === 'active') return 'active'
  return tab === 'closed' ? 'closed' : 'open'
}

const VenuesPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()
  const [venues, setVenues] = useState<VenueDto[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || 'name'
  const currentSortOrder = searchParams.get('sortOrder') || 'asc'
  const currentStatusParam = searchParams.get('status') || ''
  const activeTab = resolveTab(currentStatusParam)
  const statusFilter = resolveStatusFilter(currentStatusParam, activeTab)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300) // 300ms delay
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (activeTab === 'suggestions') {
      return
    }

    const fetchVenues = async () => {
      try {
        const response = await adminService.getVenues(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
          statusFilter
        )
        setVenues(response.venues)
        setPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch venues', err)
      } finally {
      }
    }

    fetchVenues()
  }, [activeTab, currentPage, statusFilter, currentPageSize, debouncedSearchValue, currentSortBy, currentSortOrder, searchParams, refreshNonce])

  const handleRowClick = (row: { publicId: string }) => {
    navigate(`/admin/venues/${row.publicId}`)
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleTabChange = (tab: VenuesTab) => {
    const newParams = new URLSearchParams(searchParams)
    const nextStatus = VENUE_TABS.find(item => item.key === tab)?.status ?? 'open'
    newParams.set('status', nextStatus)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  return (
    <div>
      <div className="venues-container">
        <div className="venues-header">
          <h2>Venues</h2>
          {activeTab !== 'suggestions' && (
            <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
          )}
        </div>

        <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '24px' }}>
            {VENUE_TABS.map(tab => {
              const classNames = ['tab']
              if (tab.key === activeTab) {
                classNames.push('active')
              }

              return (
                <button
                  key={tab.key}
                  type="button"
                  className={classNames.join(' ')}
                  onClick={() => handleTabChange(tab.key)}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
          {activeTab !== 'suggestions' && (
            <button
              onClick={() => setIsVenueModalOpen(true)}
              className="styled-button sm"
            >
              <span>Add Venue</span>
            </button>
          )}
        </div>

        {activeTab === 'suggestions' ? (
          <VenueSuggestionsTable />
        ) : (
          <div className="venues-table-container">
            <div className={`table-wrapper`}>
              <Table
                id={activeTab === 'closed' ? 'closed-venues' : 'venues'}
                name={activeTab === 'closed' ? 'Closed venues' : 'Active venues'}
                keyField="publicId"
                initialSortColumn={currentSortBy}
                initialSortOrder={currentSortOrder}
                columns={[
                  { label: '', accessor: 'logoUrl', type: 'venueAvatar' },
                  { label: 'Name', accessor: 'name', type: 'nameAndAddress' },
                  { label: 'Visits', accessor: 'visits', type: 'number' },
                  { label: 'Peeks', accessor: 'peeks', type: 'number' },
                  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                ]}
                sourceData={venues}
                isPropertyBarVisible={false}
                filterTerms=""
                onRowClick={handleRowClick}
                onSortChange={handleSortChange}
                disableGlobalSearch={true}
                disableClientSort={true}
              />
            </div>
          </div>
        )}
      </div>
      <AddVenueModal
        isOpen={isVenueModalOpen}
        onClose={() => setIsVenueModalOpen(false)}
        onCreated={() => setRefreshNonce(value => value + 1)}
      />
    </div>
  )
}

export default VenuesPage
